import { useEffect } from 'react';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { useCookies } from 'react-cookie';
import queryString from 'query-string';

import * as analytics from '../lib/analytics';
import Script from 'next/script';

const siteUrl =
  process.env.NEXT_PUBLIC_SITE_URL ??
  process.env.NEXT_PUBLIC_VERCEL_URL ??
  'http://localhost:3000';

const useSetUtmCookies = () => {
  const [cookies, setCookie] = useCookies();

  useEffect(() => {
    const queryParams = queryString.parse(location.search);
    const fields = [
      'utm_campaign',
      'utm_content',
      'utm_medium',
      'utm_source',
      'utm_term',
      'gclid',
    ];

    fields.forEach((field) => {
      if (!cookies[field] && queryParams[field]) {
        setCookie(field, queryParams[field]);
      }
    });
  }, [cookies, setCookie]);
};

interface PageMetadataProps {
  pageTitle?: string;
  description?: string;
  openGraphImage?: string;
}

export const PageMetadata: React.FC<PageMetadataProps> = ({
  pageTitle = 'Authorized Blue Cross Health Insurance Agency',
  description = `We are trusted advisors for helping you find the right medical insurance plan to meet the needs and budget of you and your family in the great State of North Carolina.`,
  openGraphImage,
}) => {
  const router = useRouter();

  useSetUtmCookies();

  useEffect(() => {
    analytics.initialize();
  }, []);

  useEffect(() => {
    analytics.logPageView({
      // path without query params or anchors
      pathname: router.asPath.split('?')[0].split('#')[0],
      searchParams: router.query,
    });
  }, [router.asPath, router.query]);

  return (
    <>
      <Head>
        <title>{`${pageTitle} | Health Plans of NC`}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={`${siteUrl}${router.asPath}`} />
        <meta
          name="facebook-domain-verification"
          content="ftql3pr41dt8ncrgv2ovtk0l152xsj"
        />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={description} />
        <meta
          property="og:image"
          content={
            openGraphImage ?? `${siteUrl}/static/images/open-graph-image.jpg`
          }
        />
        <meta property="og:url" content={`${siteUrl}${router.asPath}`} />
        <link rel="shortcut icon" href="/static/favicon/favicon.ico" />
        <link
          rel="apple-touch-icon"
          sizes="180x180"
          href="/static/favicon/apple-touch-icon.png"
        />
        <link
          rel="icon"
          type="image/png"
          sizes="32x32"
          href="/static/favicon/favicon-32x32.png"
        />
        <link
          rel="icon"
          type="image/png"
          sizes="16x16"
          href="/static/favicon/favicon-16x16.png"
        />
      </Head>
      <Script id="Google Tag Manager" strategy="afterInteractive">
        {`(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer','GTM-T6SJ88D');`}
      </Script>
    </>
  );
};
